import { Button, FileUpload, FormField } from '@wix/design-system';
import React, { useContext } from 'react';

import { EditorContext, RicosContext, useDevService } from './../../ricos-context';
import type { Helpers } from './../../ricos-types';
import type { LogBiFn } from './../../_shared/bi-context-provider/bi-context-provider';
import type { UpdateService } from './../../_shared/upload/update-service';
import type { IUploadService } from './../../_shared/upload/upload-service';
import { MEDIA_POPOVERS_BUTTONS_NAMES_BI } from './../../wix-rich-content-common';

import { UPLOAD_TAB_UPLOAD_BUTTON } from '../data-hooks';
import { handleVideoUpload } from '../handle-video-upload';
import type { VideoPluginEditorConfig } from '../types';
import { VIDEO_TYPE } from '../types';

const FILE_INPUT_ACCEPT = 'video/*';

type UploadVideoProps = {
  nodeId?: string;
  helpers: Helpers;
  enableCustomUploadOnMobile?: boolean;
  uploadService?: IUploadService;
  updateService?: UpdateService;
  closeModal: () => void;
  logBi: LogBiFn;
} & Pick<
  VideoPluginEditorConfig,
  'disableDownloadDefault' | 'handleFileSelection' | 'handleFileUpload'
>;

export const UploadVideo: React.FC<UploadVideoProps> = ({
  nodeId,
  helpers,
  enableCustomUploadOnMobile,
  uploadService,
  closeModal,
  disableDownloadDefault,
  handleFileSelection,
  handleFileUpload,
  logBi,
}) => {
  const { t, isMobile } = useContext(RicosContext);
  const { getPublicCommands } = useContext(EditorContext);

  const devService = useDevService();
  const hasCustomFileUpload = Boolean(handleFileUpload || handleFileSelection);
  const showUploadSection = (!isMobile || enableCustomUploadOnMobile) && hasCustomFileUpload;

  const handleUploadClick = async () => {
    helpers?.onPluginsPopOverClick?.({
      pluginId: VIDEO_TYPE,
      buttonName: MEDIA_POPOVERS_BUTTONS_NAMES_BI.upload,
    });

    logBi({ dataHook: UPLOAD_TAB_UPLOAD_BUTTON });

    handleVideoUpload({
      services: { uploadService: uploadService!, devService },
      config: { disableDownloadDefault, handleFileSelection, handleFileUpload },
      publicCommands: getPublicCommands(),
      nodeId,
    });

    closeModal();
  };

  return (
    <FormField label={t('VideoCustomUploadModal_Title')} maxLines={2}>
      {showUploadSection ? (
        <FileUpload onChange={() => {}} accept={FILE_INPUT_ACCEPT}>
          {() => (
            <Button
              dataHook={UPLOAD_TAB_UPLOAD_BUTTON}
              onClick={handleUploadClick}
              fullWidth
              size="small"
            >
              {t('VideoUploadModal_CustomVideoClickText')}
            </Button>
          )}
        </FileUpload>
      ) : null}
    </FormField>
  );
};
